import { useAtom, useAtomValue } from 'jotai'
import { useNavigate } from 'react-router-dom'
import SmartButton from 'src/common/components/Buttons/SmartButton'
import OfferOTCSettings from 'src/common/components/OfferOTCSettings/OfferOTCSettings'
import useEffectCompare from 'src/common/hooks/helpers/useEffectCompare'
import useToggle from 'src/common/hooks/helpers/useToggle'
import Fade from 'src/common/ui-kit/Animations/Fade'
import ExpiryBadges from '../../../../common/components/ExpiryBadges/ExpiryBadges'
import useUpdateTradeAmounts from '../../hooks/useUpdateTradeAmounts'
import {
  sellTokenAtom,
  sellTokenAmountAtom,
  buyTokenAtom,
  buyTokenAmountAtom,
  limitOrderOptionsAtom,
} from '../../stores/trade-store'
import TokenPairSelect from '../TokenPairSelect'
import TradePricePerToken from '../TradePricePerToken'
import { OfferOTCSettings as IOfferOTCSettings } from 'src/common/components/OfferOTCSettings/OfferOTCSettingsList'
import useBalance from 'src/common/contracts/hooks/token/useBalance'
import { useTranslation } from 'react-i18next'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'

const LimitOrder = () => {
  const { t } = useTranslation('trade')

  const { smartAccountInfo } = useSmartAccount()

  const sellToken = useAtomValue(sellTokenAtom)
  const sellTokenAmount = useAtomValue(sellTokenAmountAtom)
  const buyToken = useAtomValue(buyTokenAtom)
  const buyTokenAmount = useAtomValue(buyTokenAmountAtom)

  const [limitOfferOptions, setLimitOfferOptions] = useAtom(
    limitOrderOptionsAtom
  )

  const { isOn: otcSettingsIsOpen, toggle: toggleOtcSettings } = useToggle()

  const navigate = useNavigate()

  const { updateSellAmount, updateBuyAmount } = useUpdateTradeAmounts(true)

  const sellTokenBalance = useBalance({
    token: sellToken,
    owner: smartAccountInfo?.address,
  })

  useEffectCompare(() => {
    if (sellToken && buyToken) {
      updateSellAmount(sellTokenAmount)
    }
  }, [sellToken, buyToken])

  const offerFilled =
    buyToken && sellToken && sellTokenAmount.gt(0) && buyTokenAmount.gt(0)
  const isEnoughFunds = sellTokenBalance.balance?.gte(sellTokenAmount)

  const handleToggleExpiryTimestamp = (value: number) => {
    setLimitOfferOptions(v => ({ ...v, expiryTimestamp: value }))
  }

  const handlePreviewClick = async () => {
    navigate('/trade/limit/review')
  }

  const offerWithNft = sellToken?.type !== 'ERC20' || buyToken?.type !== 'ERC20'

  const handleChangeSettings = (settings: IOfferOTCSettings) => {
    setLimitOfferOptions(prev => ({ ...prev, ...settings }))
  }

  return (
    <div className='flex flex-col gap-2 relative'>
      <TokenPairSelect
        onBuyTokenAmountChange={updateBuyAmount}
        onSellTokenAmountChange={updateSellAmount}
        excludeNFT
      />
      {buyToken && sellToken && (
        <Fade>
          <TradePricePerToken />
        </Fade>
      )}
      <div className='flex items-center justify-between gap-3 paper p-4'>
        <div className='typo-label2'>Expire:</div>
        <ExpiryBadges
          onSelect={handleToggleExpiryTimestamp}
          expiryTimestamp={limitOfferOptions.expiryTimestamp}
        />
      </div>
      <OfferOTCSettings
        className='col-span-12'
        open={otcSettingsIsOpen}
        onToggle={toggleOtcSettings}
        offerWithNft={offerWithNft}
        settings={limitOfferOptions}
        onChangeSettings={handleChangeSettings}
      />
      <SmartButton
        className='w-full px-2'
        disabled={
          !buyToken || !smartAccountInfo || !offerFilled || !isEnoughFunds
        }
        onClick={handlePreviewClick}
      >
        {!buyToken
          ? t('selectToken')
          : isEnoughFunds || !offerFilled
          ? t('limitOrder.previewLimitOrder')
          : t('limitOrder.insufficientFunds')}
      </SmartButton>
    </div>
  )
}

export default LimitOrder
